import { useState, useEffect, useRef } from "react";
import { Scanner } from "@yudiel/react-qr-scanner";
import { Camera, CameraResultType } from '@capacitor/camera';
import { Capacitor } from '@capacitor/core';
import { Haptics, ImpactStyle } from '@capacitor/haptics';

// Animated corner brackets
const CornerBracket = ({ position }: { position: 'tl' | 'tr' | 'bl' | 'br' }) => {
  const classes: Record<string, string> = {
    tl: 'top-0 left-0 border-t-[3px] border-l-[3px] rounded-tl-2xl',
    tr: 'top-0 right-0 border-t-[3px] border-r-[3px] rounded-tr-2xl',
    bl: 'bottom-0 left-0 border-b-[3px] border-l-[3px] rounded-bl-2xl',
    br: 'bottom-0 right-0 border-b-[3px] border-r-[3px] rounded-br-2xl',
  };
  return (
    <div
      className={`absolute w-10 h-10 border-blue-400 ${classes[position]} transition-all duration-500`}
      style={{ filter: 'drop-shadow(0 0 6px rgba(96,165,250,0.9))' }}
    />
  );
};

const ScannerOverlay = ({ isScanning }: { isScanning: boolean }) => (
  <div className="absolute inset-0 pointer-events-none z-10 flex items-center justify-center">
    {/* Dark vignette around edges, transparent center */}
    <div
      className="absolute inset-0"
      style={{
        background: 'radial-gradient(ellipse 55% 55% at 50% 50%, transparent 0%, rgba(0,0,0,0.65) 100%)',
      }}
    />

    {/* Scan Frame */}
    <div className="relative w-60 h-60">
      {/* Subtle background dim inside box */}
      <div className="absolute inset-0 border border-white/10 rounded-2xl" />

      {/* Corner Brackets */}
      <CornerBracket position="tl" />
      <CornerBracket position="tr" />
      <CornerBracket position="bl" />
      <CornerBracket position="br" />

      {/* Animated Laser Scan Line */}
      {isScanning && (
        <div
          className="absolute left-2 right-2 h-[2px] rounded-full animate-scanline z-20"
          style={{
            background: 'linear-gradient(90deg, transparent, rgba(96,165,250,0.3), #60a5fa, rgba(96,165,250,0.3), transparent)',
            boxShadow: '0 0 12px 2px rgba(96,165,250,0.7)',
          }}
        />
      )}
    </div>

    {/* Bottom hint text */}
    <div className="absolute bottom-6 left-0 right-0 flex flex-col items-center gap-1 px-6">
      <div className="flex items-center gap-2">
        <div
          className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse"
          style={{ boxShadow: '0 0 6px rgba(96,165,250,0.8)' }}
        />
        <p className="text-white/80 text-[11px] font-semibold tracking-widest uppercase">
          Point at QR Code
        </p>
        <div
          className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse"
          style={{ boxShadow: '0 0 6px rgba(96,165,250,0.8)', animationDelay: '0.3s' }}
        />
      </div>
    </div>
  </div>
);

export default function QrScan({
  onScan,
}: {
  onScan: (value: string) => void;
}) {
  const [result, setResult] = useState("");
  const [error, setError] = useState<string>("");
  const [hasPermission, setHasPermission] = useState<boolean | null>(null);
  const [isScanning, setIsScanning] = useState(true);
  const isNative = Capacitor.isNativePlatform();

  useEffect(() => {
    checkCameraPermission();
  }, []);

  const checkCameraPermission = async () => {
    try {
      if (isNative) {
        const permission = await Camera.requestPermissions({ permissions: ['camera'] });
        if (permission.camera === 'granted') {
          setHasPermission(true);
          setError("");
        } else {
          setHasPermission(false);
          setError("Camera permission denied. Please enable it in system settings.");
        }
      } else {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment" }
        });
        stream.getTracks().forEach(track => track.stop());
        setHasPermission(true);
        setError("");
      }
    } catch (err: any) {
      console.error("Camera permission error:", err);
      setHasPermission(false);
      setError("Failed to access camera. Please check permissions.");
    }
  };

  const handleScanSuccess = async (qrCodeText: string) => {
    if (!qrCodeText || result) return;

    // Physical feedback
    if (isNative) {
      await Haptics.impact({ style: ImpactStyle.Heavy });
    } else if ('vibrate' in navigator) {
      navigator.vibrate([50, 30, 50]);
    }

    setIsScanning(false);
    setResult(qrCodeText);
    setError("");

    // Brief pause to show success animation before navigating
    setTimeout(() => {
      onScan(qrCodeText);
    }, 600);
  };

  const handleError = (err: Error) => {
    console.error("QR Scanner error:", err);
    setError("Scanner error. Please try again.");
  };

  // --- Loading State ---
  if (hasPermission === null) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[300px] bg-gray-950 rounded-3xl gap-5">
        {/* Pulsing camera rings */}
        <div className="relative w-20 h-20 flex items-center justify-center">
          <div className="absolute inset-0 rounded-full border-2 border-blue-500/20 animate-ping" />
          <div className="absolute inset-2 rounded-full border-2 border-blue-500/30 animate-ping" style={{ animationDelay: '0.2s' }} />
          <div className="w-10 h-10 rounded-full bg-blue-600/20 flex items-center justify-center">
            <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </div>
        </div>
        <div className="text-center">
          <p className="text-white/70 font-bold text-xs uppercase tracking-[0.25em]">Starting Camera</p>
          <p className="text-white/30 text-[10px] mt-1">Please wait...</p>
        </div>
      </div>
    );
  }

  // --- Permission Denied State ---
  if (hasPermission === false) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[300px] bg-gray-950 rounded-3xl p-8 text-center gap-5">
        <div className="w-16 h-16 bg-red-500/10 border border-red-500/20 rounded-2xl flex items-center justify-center">
          <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
              d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
          </svg>
        </div>
        <div>
          <h3 className="font-black text-white text-base mb-1">Camera Access Needed</h3>
          <p className="text-white/40 text-xs leading-relaxed max-w-[240px]">{error}</p>
        </div>
        <button
          onClick={checkCameraPermission}
          className="w-full bg-blue-600 text-white font-bold py-3.5 rounded-2xl text-sm tracking-wide hover:bg-blue-500 active:scale-95 transition-all"
        >
          Enable Camera
        </button>
      </div>
    );
  }

  // --- Scanner Active State ---
  return (
    <div className="relative overflow-hidden rounded-3xl bg-black">
      {/* Scanner Video Feed — fill the container */}
      <div className="aspect-square w-full relative">
        <Scanner
          constraints={{
            video: {
              facingMode: { ideal: "environment" },
            }
          }}
          onScan={(detectedCodes) => {
            if (detectedCodes && detectedCodes.length > 0 && !result) {
              handleScanSuccess(detectedCodes[0].rawValue);
            }
          }}
          onError={handleError}
          styles={{
            container: { width: '100%', height: '100%', position: 'absolute', inset: 0 },
            video: { width: '100%', height: '100%', objectFit: 'cover' },
          }}
        />

        {/* Overlay (only shown while scanning) */}
        {!result && !error && <ScannerOverlay isScanning={isScanning} />}

        {/* Error / Retry State */}
        {error && (
          <div className="absolute inset-0 z-40 flex flex-col items-center justify-center bg-black/90 p-6 text-center gap-3">
            <p className="text-red-400 text-xs font-semibold max-w-[260px]">{error}</p>
            <button
              onClick={() => { setError(""); setIsScanning(true); checkCameraPermission(); }}
              className="px-4 py-2 bg-blue-600 text-white rounded-xl text-xs font-bold hover:bg-blue-500 active:scale-95 transition-all shadow-lg"
            >
              Retry Camera
            </button>
          </div>
        )}

        {/* ✅ Success State */}
        {result && (
          <div className="absolute inset-0 z-30 flex flex-col items-center justify-center bg-black/80 backdrop-blur-sm">
            {/* Animated checkmark circle */}
            <div className="relative mb-5">
              {/* Ripple rings */}
              <div className="absolute -inset-4 rounded-full bg-green-500/20 animate-ping" />
              <div className="absolute -inset-2 rounded-full bg-green-500/10 animate-ping" style={{ animationDelay: '0.15s' }} />
              {/* Circle */}
              <div className="relative w-20 h-20 rounded-full bg-green-500 flex items-center justify-center shadow-2xl"
                style={{ boxShadow: '0 0 40px rgba(34,197,94,0.5)' }}>
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
            <p className="text-white font-black text-base uppercase tracking-widest">Shop Found!</p>
            <p className="text-green-400/70 text-[11px] mt-1 font-semibold uppercase tracking-wider">Connecting...</p>
          </div>
        )}

        {/* ❌ Inline Error Banner */}
        {error && !result && (
          <div className="absolute top-3 left-3 right-3 z-40">
            <div className="bg-red-600/90 backdrop-blur-md border border-white/10 rounded-xl px-4 py-2.5 flex items-center gap-3">
              <svg className="w-4 h-4 text-white shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
              </svg>
              <p className="text-white text-[11px] font-bold uppercase tracking-wide">{error}</p>
              <button
                className="ml-auto text-white/60 hover:text-white text-xs font-bold"
                onClick={() => setError("")}
              >
                ✕
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
